// client/src/hooks/useAlerts.ts
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import type { SpendingAlert } from '../components/agent/types';

interface UseAlertsReturn {
  alerts: SpendingAlert[];
  loading: boolean;
  error: string | null;
  unreadCount: number;
  refresh: () => Promise<void>;
  markAsRead: (alertId: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
}

export function useAlerts(limit = 20): UseAlertsReturn {
  const [alerts, setAlerts] = useState<SpendingAlert[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAlerts = useCallback(async () => {
    try {
      setError(null);
      const response = await axios.get('/api/agent/alerts', {
        params: { limit },
      });
      setAlerts(response.data.alerts || []);
    } catch (err) {
      if (axios.isAxiosError(err)) {
        setError(err.response?.data?.error?.message || 'Failed to fetch alerts');
      } else {
        setError('An unexpected error occurred');
      }
    } finally {
      setLoading(false);
    }
  }, [limit]);

  const markAsRead = useCallback(async (alertId: string) => {
    try {
      await axios.patch(`/api/agent/alerts/${alertId}`, { status: 'read' });
      setAlerts(prev =>
        prev.map(alert =>
          alert.id === alertId ? { ...alert, status: 'read' } : alert
        )
      );
    } catch (err) {
      console.error('Failed to mark alert as read:', err);
    }
  }, []);

  const markAllAsRead = useCallback(async () => {
    try {
      const unreadAlerts = alerts.filter(a => a.status === 'unread');
      await Promise.all(
        unreadAlerts.map(alert =>
          axios.patch(`/api/agent/alerts/${alert.id}`, { status: 'read' })
        )
      );
      setAlerts(prev =>
        prev.map(alert => ({ ...alert, status: 'read' }))
      );
    } catch (err) {
      console.error('Failed to mark all alerts as read:', err);
    }
  }, [alerts]);

  useEffect(() => {
    fetchAlerts();
  }, [fetchAlerts]);

  const unreadCount = alerts.filter(a => a.status === 'unread').length;

  return {
    alerts,
    loading,
    error,
    unreadCount,
    refresh: fetchAlerts,
    markAsRead,
    markAllAsRead,
  };
}